var _Dash;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import { textFieldClasses } from '@mui/material/TextField';
import { iconButtonClasses } from '@mui/material/IconButton';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { formHelperTextClasses } from '@mui/material/FormHelperText';
import { bool, func, instanceOf, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { getDateRangeMessage, getIconSizes } from '../internals/utils';
import SovosToolbarGroup from '../sovos-toolbar-group';
import SovosDatePicker from '../sovos-date-picker';
import SovosSnackbar from '../sovos-snackbar';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const StyledToolbarGroup = styled(SovosToolbarGroup)(_ref => {
  let {
    theme: {
      spacing
    }
  } = _ref;
  return {
    paddingLeft: spacing(0.5),
    paddingRight: spacing(0.5)
  };
});
const StyledDatePicker = styled(SovosDatePicker)(_ref2 => {
  let {
    disabled,
    theme: {
      palette,
      spacing
    }
  } = _ref2;
  return {
    ["&.".concat(textFieldClasses.root)]: {
      width: spacing(15)
    },
    ["& .".concat(inputBaseClasses.root)]: {
      '&:before': {
        borderWidth: 0
      },
      '&:hover:before': {
        borderWidth: 1,
        borderBottomColor: palette.text.secondary
      },
      '& input': {
        '&::placeholder': {
          opacity: 1
        }
      },
      ["&.".concat(inputBaseClasses.focused)]: {
        '& input': {
          '&::placeholder': {
            color: palette.text.secondary
          }
        }
      },
      ["&.".concat(inputBaseClasses.error)]: {
        color: palette.text.primary,
        '&::before, &::after, &:hover::before, &:hover::after': {
          borderWidth: 0
        },
        // override the adornment color for errors
        ["& .".concat(iconButtonClasses.root)]: {
          '& svg': {
            color: palette.error.main
          }
        }
      }
    },
    ["& .".concat(inputAdornmentClasses.root)]: {
      marginRight: 0,
      ["& .".concat(iconButtonClasses.root)]: {
        padding: 0,
        marginLeft: 0,
        ...getIconSizes(spacing).small.button,
        '& svg': {
          color: disabled ? palette.action.disabled : palette.text.primary,
          ...getIconSizes(spacing).small.icon
        }
      }
    },
    // hide inline error messages
    ["& .".concat(formHelperTextClasses.root)]: {
      display: 'none'
    }
  };
});
const Dash = styled('div')(_ref3 => {
  let {
    theme: {
      palette,
      spacing
    }
  } = _ref3;
  return {
    height: 0,
    width: spacing(2),
    borderTop: "1px solid ".concat(palette.text.primary),
    margin: "0 ".concat(spacing(0.5))
  };
});

/**
 * **Note**: must be a child of `SovosToolbar`
 */
const SovosToolbarDateSelector = _ref4 => {
  let {
    className,
    'data-testid': dataTestId,
    disabled = false,
    format: formatProps,
    fromLabel,
    maxDate,
    minDate,
    onChange,
    toLabel,
    invalidEndDateMessage: invalidEndDateMessageProp,
    invalidStartDateMessage: invalidStartDateMessageProp,
    endDateAfterStartDateMessage: endDateAfterStartDateMessageProp,
    values,
    ...rest
  } = _ref4;
  const {
    t
  } = useMosaicTranslation();
  const format = formatProps || t('toolbarDateSelector.format');
  const invalidStartDateMessage = invalidStartDateMessageProp || t('toolbarDateSelector.invalidStartDateMessage');
  const invalidEndDateMessage = invalidEndDateMessageProp || t('toolbarDateSelector.invalidEndDateMessage');
  const endDateAfterStartDateMessage = endDateAfterStartDateMessageProp || t('toolbarDateSelector.endDateAfterStartDateMessage');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [fromPlaceholder, setFromPlaceholder] = React.useState(fromLabel);
  const [toPlaceholder, setToPlaceholder] = React.useState(toLabel);
  React.useEffect(() => {
    setErrorMessage(getDateRangeMessage({
      values,
      format,
      minDate,
      maxDate,
      invalidStartDateMessage,
      invalidEndDateMessage,
      endDateAfterStartDateMessage
    }));
  }, [values, format, maxDate, minDate, invalidEndDateMessage, invalidStartDateMessage, endDateAfterStartDateMessage]);
  const pickerProps = {
    format,
    InputAdornmentProps: {
      position: 'start'
    }
  };
  const TextFieldProps = {
    variant: 'standard',
    placeholder: 'place'
  };
  return /*#__PURE__*/React.createElement(StyledToolbarGroup, _extends({
    className: clsx('sovosToolbarDateSelector', className),
    "data-testid": dataTestId
  }, rest), /*#__PURE__*/React.createElement(StyledDatePicker, _extends({
    format: format || t('toolbarDateSelector.format'),
    disabled: disabled
  }, pickerProps, {
    maxDate: values.to || maxDate,
    minDate: minDate,
    name: "from",
    onChange: newValue => {
      onChange({
        from: newValue,
        to: values.to
      });
    },
    TextFieldProps: {
      ...TextFieldProps,
      inputProps: {
        'aria-label': fromLabel,
        onBlur: () => setFromPlaceholder(fromLabel),
        onFocus: () => setFromPlaceholder(format.toLowerCase()),
        placeholder: fromPlaceholder || t('toolbarDateSelector.fromLabel')
      }
    },
    value: values.from
  })), _Dash || (_Dash = /*#__PURE__*/React.createElement(Dash, null)), /*#__PURE__*/React.createElement(StyledDatePicker, _extends({
    format: format || t('toolbarDateSelector.format'),
    disabled: disabled
  }, pickerProps, {
    maxDate: maxDate,
    minDate: values.from || minDate,
    name: "to",
    onChange: newValue => {
      onChange({
        from: values.from,
        to: newValue
      });
    },
    TextFieldProps: {
      ...TextFieldProps,
      inputProps: {
        'aria-label': toLabel,
        onBlur: () => setToPlaceholder(toLabel),
        onFocus: () => setToPlaceholder(format.toLowerCase()),
        placeholder: toPlaceholder || t('toolbarDateSelector.toLabel')
      }
    },
    value: values.to
  })), /*#__PURE__*/React.createElement(SovosSnackbar, {
    message: errorMessage,
    open: errorMessage.length > 0,
    onClose: () => setErrorMessage(''),
    variant: "error"
  }));
};
SovosToolbarDateSelector.propTypes = {
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * If `true`, the button is disabled
   */
  disabled: bool,
  /**
   * Text label for the error indicating a `from` value occurs after the `to` value
   */
  endDateAfterStartDateMessage: string,
  /**
   * Format of a valid date i.e.: `DD/MM/YYYY`
   */
  format: string,
  /**
   * Label for 'from' field
   */
  fromLabel: string,
  /**
   * Text label for the error indicating an invalid `to` value
   */
  invalidEndDateMessage: string,
  /**
   * Text label for the error indicating an invalid `from` value
   */
  invalidStartDateMessage: string,
  /**
   * Date instance or string value of the maximum `to` value
   */
  maxDate: oneOfType([instanceOf(Date), string]),
  /**
   * Date instance or string value of the minimum `from` value
   */
  minDate: oneOfType([instanceOf(Date), string]),
  /**
   * Callback fired when the value is changed `function(data: object) => void`
   */
  onChange: func.isRequired,
  /**
   * Label for `to` field
   */
  toLabel: string,
  /**
   * Object with string properties for `from` and `to` values
   */
  values: shape({
    from: string,
    to: string
  }).isRequired
};
export default SovosToolbarDateSelector;